import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { formatMarkdown, isMarkdown, escapeHtml } from "./AISummary";

const useStyles = makeStyles({
  root: {
    display: "none",
    "@media print": {
      display: "block",
      color: "#000",
      backgroundColor: "#fff",
      padding: "24px",
      fontFamily: "Arial, sans-serif",
    },
  },
  header: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "2px solid #000",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.9rem",
    marginBottom: 4,
  },
  title: {
    fontSize: "1.4rem",
    fontWeight: 600,
    marginBottom: 8,
  },
  disclaimer: {
    fontSize: "0.8rem",
    fontStyle: "italic",
    marginBottom: 16,
    color: "#333",
  },
  content: {
    fontSize: "0.95rem",
    lineHeight: 1.5,
    "& table": { pageBreakInside: "avoid", width: "100%", borderCollapse: "collapse" },
    "& th, & td": { border: "1px solid #999", padding: "6px 8px" },
    "& ul, & ol": { paddingLeft: 24 },
    "& h1, & h2, & h3": { pageBreakAfter: "avoid" },
  },
});

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const AISummaryPrintable = React.forwardRef(({ summary, patientInfo = {} }, ref) => {
  const classes = useStyles();
  const { firstName, lastname, dateOfBirth, id } = patientInfo;
  const patientName = [firstName, lastname].filter(Boolean).join(" ") || "N/A";
  const isMarkdownFormat = isMarkdown(summary);
  const formattedContent = isMarkdownFormat
    ? formatMarkdown(summary)
    : `<p>${escapeHtml(summary || "")}</p>`;

  return (
    <div ref={ref} className={classes.root}>
      <div className={classes.header}>
        <div className={classes.title}>AI Health Analysis Report</div>
        <div className={classes.headerRow}>
          <span>Patient: {patientName}</span>
          <span>Record ID: {id || "N/A"}</span>
        </div>
        <div className={classes.headerRow}>
          <span>Date of Birth: {formatDate(dateOfBirth)}</span>
          <span>Report generated: {formatDate(new Date())}</span>
        </div>
      </div>
      <div className={classes.disclaimer}>
        This AI-generated summary is based on the patient&apos;s health record
        data from the last 7 days. It should be used as a supplementary tool
        and not as a replacement for professional medical judgment.
      </div>
      <div
        className={classes.content}
        dangerouslySetInnerHTML={{ __html: formattedContent }}
      />
    </div>
  );
});

AISummaryPrintable.displayName = "AISummaryPrintable";

export default AISummaryPrintable;
